<template>
  <footer class="bg-gradient-footer relative overflow-hidden">
    <!-- Subtle hex pattern overlay -->
    <div class="absolute inset-0 hex-pattern opacity-30 pointer-events-none"></div>

    <!-- Top glow -->
    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/20 rounded-full blur-[100px] pointer-events-none"></div>

    <!-- ── Main Content ──────────────────────────────────────────── -->
    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">

        <!-- Column 1: Brand -->
        <div class="lg:col-span-4">
          <RouterLink to="/" class="flex items-center gap-3 mb-5 group">
            <div class="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors duration-300">
              <img src="/assets/images/logo/orel-logo-white.png" class="h-8 w-auto" alt="Orel Medical Supplies" />
            </div>
            <div>
              <div class="font-bold text-white text-sm tracking-wide">OREL MEDICAL</div>
              <div class="text-white/40 text-[10px] uppercase tracking-widest">Supplies Limited</div>
            </div>
          </RouterLink>

          <p class="text-white/55 text-sm leading-relaxed mb-6 max-w-xs">
            OREL MEDICAL SUPPLIES LIMITED - Engineering with Integrity. Your trusted partner in delivering superior healthcare solutions across Uganda.
          </p>

          <!-- Social links -->
          <div class="flex items-center gap-2.5">
            <a
              v-for="s in socials"
              :key="s.href"
              :href="s.href"
              target="_blank"
              rel="noopener"
              :aria-label="s.label"
              class="w-9 h-9 rounded-lg bg-white/8 hover:bg-primary border border-white/10 hover:border-primary flex items-center justify-center text-white/60 hover:text-white transition-all duration-300"
            >
              <i :class="['text-xs', s.icon]"></i>
            </a>
          </div>
        </div>

        <!-- Column 2: Quick Links -->
        <div class="lg:col-span-2">
          <h4 class="text-white font-semibold text-xs uppercase tracking-[0.15em] mb-5 flex items-center gap-2">
            <span class="w-4 h-0.5 bg-secondary rounded-full"></span>
            Quick Links
          </h4>
          <ul class="space-y-3">
            <li v-for="link in quickLinks" :key="link.to">
              <RouterLink
                :to="link.to"
                class="text-white/55 hover:text-secondary text-sm flex items-center gap-2 transition-colors duration-200 group"
              >
                <i class="fa-solid fa-chevron-right text-[9px] text-secondary/50 group-hover:text-secondary transition-colors duration-200"></i>
                {{ link.label }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <!-- Column 3: More Links -->
        <div class="lg:col-span-2">
          <h4 class="text-white font-semibold text-xs uppercase tracking-[0.15em] mb-5 flex items-center gap-2">
            <span class="w-4 h-0.5 bg-secondary rounded-full"></span>
            More Links
          </h4>
          <ul class="space-y-3">
            <li v-for="link in moreLinks" :key="link.to">
              <RouterLink
                :to="link.to"
                class="text-white/55 hover:text-secondary text-sm flex items-center gap-2 transition-colors duration-200 group"
              >
                <i class="fa-solid fa-chevron-right text-[9px] text-secondary/50 group-hover:text-secondary transition-colors duration-200"></i>
                {{ link.label }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <!-- Column 4: Contact Info -->
        <div class="lg:col-span-4">
          <h4 class="text-white font-semibold text-xs uppercase tracking-[0.15em] mb-5 flex items-center gap-2">
            <span class="w-4 h-0.5 bg-secondary rounded-full"></span>
            Contact Info
          </h4>
          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-lg bg-white/8 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i class="fa-solid fa-location-dot text-secondary text-xs"></i>
              </div>
              <div>
                <p class="text-white/40 text-xs uppercase tracking-wider mb-0.5">Address</p>
                <p class="text-white/70 text-sm leading-relaxed">Rubaga Road, Butikiro Complex</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-lg bg-white/8 border border-white/10 flex items-center justify-center flex-shrink-0">
                <i class="fa-solid fa-phone text-secondary text-xs"></i>
              </div>
              <div>
                <p class="text-white/40 text-xs uppercase tracking-wider mb-0.5">Phone</p>
                <a href="tel:+256779266772" class="text-white/70 hover:text-secondary text-sm transition-colors duration-200">+256779266772/+256769571216</a>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-lg bg-white/8 border border-white/10 flex items-center justify-center flex-shrink-0">
                <i class="fa-solid fa-envelope text-secondary text-xs"></i>
              </div>
              <div>
                <p class="text-white/40 text-xs uppercase tracking-wider mb-0.5">Email</p>
                <a href="mailto:orelmedicalsupplies@gmail.com" class="text-white/70 hover:text-secondary text-sm transition-colors duration-200">orelmedicalsupplies@gmail.com</a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ── Bottom Bar ────────────────────────────────────────────── -->
    <div class="relative border-t border-white/8">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div class="flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
          <p class="text-white/40 text-xs">
            © Orel Medical Suppliers {{ year }}. All Rights Reserved.
          </p>
          <p class="text-white/25 text-xs">
            <a href="https://www.intanode.co.ug" target="_blank" rel="noopener" class="hover:text-secondary transition-colors duration-200">
              Courtesy of Intanode Technologies Ltd
            </a>
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
const year = new Date().getFullYear()

const socials = [
  { href: 'https://www.facebook.com/', icon: 'fa-brands fa-facebook-f',  label: 'Facebook' },
  { href: 'https://www.linkedin.com/', icon: 'fa-brands fa-linkedin-in', label: 'LinkedIn' },
  { href: 'https://twitter.com/',      icon: 'fa-brands fa-twitter',     label: 'Twitter'  },
  { href: 'https://www.instagram.com/',icon: 'fa-brands fa-instagram',   label: 'Instagram'},
]

const quickLinks = [
  { to: '/',            label: 'Home'         },
  { to: '/about-us',    label: 'About Us'     },
  { to: '/services',    label: 'Our Services' },
  { to: '/our-products',label: 'Our Products' },
]

const moreLinks = [
  { to: '/contact-us', label: 'Contact Us' },
  { to: '/faqs',       label: 'FAQs'       },
]
</script>
